/**
 * The local bridge to external agents. `tools/mcp-server.ts` runs on the user's machine as
 * an MCP server for Claude Code, Claude Desktop or any other client and opens a WebSocket
 * on 127.0.0.1; when the user enables it in the panel, this page connects, advertises its
 * tools and executes the calls the server forwards — the same executors the chat uses.
 */
import { store } from '../state/store';
import { runTool, type CommandMetadata, type ToolOutcome, type ToolServices } from './toolRuntime';
import { TOOL_DEFINITIONS } from './toolSchemas';
import type { BridgeStatus } from './runtime';
import { t } from '../i18n/I18n';

export const BRIDGE_URL = 'ws://127.0.0.1:5180';

export interface BridgeOptions {
  url?: string;
  services?: () => ToolServices;
  onStatus?: (status: BridgeStatus) => void;
  /** Called before a tool runs; the returned function records the outcome. */
  onCall?: (name: string, input: unknown) => (outcome: ToolOutcome) => void;
  onIdle?: () => void;
  socketFactory?: (url: string) => WebSocket;
}

type Incoming = { type?: string; id?: string; name?: string; input?: unknown; command?: unknown };
const bridgeTools = TOOL_DEFINITIONS.filter(tool => tool.group !== 'agents');
const bridgeToolNames = bridgeTools.map(tool => tool.name);

function externalCommand(value: unknown): CommandMetadata | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return value as CommandMetadata | undefined;
  const { turnId: _untrustedOwner, ...command } = value as Record<string, unknown>;
  return command as unknown as CommandMetadata;
}

export class AgentBridge {
  private socket: WebSocket | null = null;
  private enabled = false;
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private inFlight = new Map<string, { socket: WebSocket; controller: AbortController }>();
  private unsubscribe: (() => void) | null = null;

  constructor(private readonly options: BridgeOptions = {}) {}

  get isEnabled(): boolean { return this.enabled; }

  enable(): void {
    if (this.enabled) return;
    this.enabled = true;
    this.attempt = 0;
    this.unsubscribe = store.subscribe(() => this.sendProject());
    this.connect();
  }

  disable(): void {
    this.enabled = false;
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    this.unsubscribe?.(); this.unsubscribe = null;
    const socket = this.socket; this.socket = null;
    this.abortCalls(socket);
    socket?.close(1000, 'disabled');
    this.options.onIdle?.();
    this.options.onStatus?.({ state: 'off' });
  }

  private connect(): void {
    if (!this.enabled) return;
    this.options.onStatus?.({ state: 'connecting', detail: this.attempt ? t('#AgentBridgeAttempt', this.attempt + 1) : undefined });
    let socket: WebSocket;
    try { socket = (this.options.socketFactory ?? (url => new WebSocket(url)))(this.options.url ?? BRIDGE_URL); }
    catch (error) { this.failed(error instanceof Error ? error.message : String(error)); return; }
    this.socket = socket;
    socket.onopen = () => {
      if (this.socket !== socket) return;
      this.attempt = 0;
      this.sendHello();
      this.options.onStatus?.({ state: 'connected' });
    };
    socket.onmessage = event => { if (this.socket === socket) void this.receive(socket, String(event.data)); };
    socket.onclose = () => {
      this.abortCalls(socket);
      if (this.socket === socket) { this.socket = null; this.failed(t('#AgentBridgeUnavailable')); }
    };
    socket.onerror = () => { /* the close that follows reports it */ };
  }

  private abortCalls(socket: WebSocket | null): void {
    for (const call of this.inFlight.values()) if (call.socket === socket) call.controller.abort();
  }

  private failed(detail: string): void {
    if (!this.enabled) return;
    this.options.onIdle?.();
    this.options.onStatus?.({ state: 'error', detail: t('#AgentBridgeFailed', detail) });
    const delay = Math.min(10_000, 1000 * 2 ** Math.min(this.attempt, 4));
    this.attempt++;
    this.timer = setTimeout(() => { this.timer = null; this.connect(); }, delay);
  }

  private project() {
    const state = store.get();
    return state.scene ? { id: state.projectId, name: state.projectName, level: state.activeLevelIndex } : null;
  }
  private send(message: unknown, socket = this.socket): void {
    if (socket && this.socket === socket && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
  }
  private sendHello(): void {
    this.send({ type: 'hello', client: 'home-planner-web', origin: typeof location !== 'undefined' ? location.origin : '', project: this.project(),
      scope: store.captureDocumentScope(),
      tools: bridgeTools.map(tool => ({ name: tool.name, description: tool.description, input_schema: tool.input_schema })) });
  }
  private lastProject = '';
  private sendProject(): void {
    const state = { project: this.project(), scope: store.captureDocumentScope() };
    const serialized = JSON.stringify(state);
    if (serialized === this.lastProject) return;
    this.lastProject = serialized;
    this.send({ type: 'project', ...state });
  }

  private async receive(socket: WebSocket, raw: string): Promise<void> {
    if (this.socket !== socket) return;
    let message: Incoming;
    try { message = JSON.parse(raw) as Incoming; } catch { return; }
    if (!message || typeof message !== 'object') return;
    if (message.type === 'ping') { this.send({ type: 'pong' }, socket); return; }
    if (message.type === 'cancel' && typeof message.id === 'string') {
      const pending = this.inFlight.get(message.id);
      if (pending?.socket === socket) pending.controller.abort();
      return;
    }
    if (message.type !== 'call' || typeof message.id !== 'string' || typeof message.name !== 'string') return;
    const { id, name, input } = message;
    if (this.inFlight.has(id)) return;
    const call = { socket, controller: new AbortController() };
    this.inFlight.set(id, call);
    let inheritedSignal: AbortSignal | undefined;
    const abort = () => call.controller.abort();
    try {
      const services = this.options.services?.() ?? {};
      inheritedSignal = services.signal;
      if (inheritedSignal?.aborted) abort();
      else inheritedSignal?.addEventListener('abort', abort, { once: true });
      const record = this.options.onCall?.(name, input);
      const outcome = await runTool(name, input ?? {}, {
        ...services, command: externalCommand(message.command), signal: call.controller.signal,
        allowedTools: services.allowedTools ? bridgeToolNames.filter(tool => services.allowedTools!.includes(tool)) : bridgeToolNames,
      });
      this.send({ type: 'result', id, isError: !!outcome.isError, content: outcome.content, summary: outcome.summary, command: outcome.command }, socket);
      try { record?.(outcome); } catch { /* Presentation cannot change an accepted result. */ }
    } catch (error) {
      this.send({ type: 'result', id, isError: true, content: [{ type: 'text', text: `The bridge could not return the outcome: ${error instanceof Error ? error.message : String(error)}. The command outcome is unknown.` }] }, socket);
    } finally {
      inheritedSignal?.removeEventListener('abort', abort);
      if (this.inFlight.get(id) === call) this.inFlight.delete(id);
      if (this.socket === socket && ![...this.inFlight.values()].some(pending => pending.socket === socket)) this.options.onIdle?.();
    }
  }
}
